import React, { useEffect } from 'react';
import { accessibleColorPairs, calculateContrastRatio, adjustColorForContrast } from '../utils/ContrastUtils';
import '../styles/AccessibilityContrast.scss';

/**
 * ContrastFixer Component
 * 
 * This component applies runtime contrast fixes to ensure that all elements
 * meet WCAG AA standards for color contrast.
 * 
 * It specifically targets the elements mentioned in the accessibility report:
 * - Get Started buttons
 * - Research Paper Writing links
 * - Page linker components (Additional Academic Resources, etc.)
 * - Testimonials links
 * - PowerPoint Presentations links
 * - Refund Guarantee links
 * - FAQ links
 * - Footer links and text
 * - Footer headings (About Us, Policies, Contact Info)
 * - Footer contact labels (Email, Phone, Hours)
 * - DMCA, Money Back Guarantee, and Cryptocurrency badges
 * - Cookie policy links in the banner
 */
const ContrastFixer: React.FC = () => {
  useEffect(() => {
    // Apply contrast fixes when the component mounts
    fixContrastIssues();

    // Re-apply fixes when the DOM changes (new content loaded, etc.)
    const observer = new MutationObserver(() => {
      fixContrastIssues();
    });

    // Watch for changes to the body element and its descendants
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Clean up the observer when the component unmounts
    return () => {
      observer.disconnect();
    };
  }, []);

  const fixContrastIssues = () => {
    // Fix primary buttons (Get Started)
    const primaryButtons = document.querySelectorAll('.btn-primary');
    primaryButtons.forEach(button => {
      button.classList.add('high-contrast-button');
      if (button instanceof HTMLElement) {
        button.style.backgroundColor = accessibleColorPairs.primary.light;
        button.style.borderColor = accessibleColorPairs.primary.light;
        button.style.color = '#ffffff';
        button.style.fontWeight = '600';
      }
    });

    // Fix page linker components
    const pageLinkers = document.querySelectorAll('.page-linker');
    pageLinkers.forEach(linker => {
      linker.classList.add('high-contrast-linker');
      if (linker instanceof HTMLElement) {
        linker.style.backgroundColor = '#f0f0f0';
        
        // Fix links inside the page linker
        const links = linker.querySelectorAll('a');
        links.forEach(link => {
          if (link instanceof HTMLElement) {
            link.style.color = accessibleColorPairs.primary.light;
            link.style.fontWeight = '600';
            link.style.textDecoration = 'underline';
          }
        });
      }
    });

    // Fix footer links, headings and text
    const footer = document.querySelector('footer.footer');
    if (footer && footer instanceof HTMLElement) {
      footer.style.backgroundColor = '#0a0a0a';
      
      // Fix all links in the footer
      const footerLinks = footer.querySelectorAll('a');
      footerLinks.forEach(link => {
        if (link instanceof HTMLElement) {
          link.style.color = '#ffffff';
          link.style.fontWeight = '500';
          // Add underline on hover for additional clarity
          link.addEventListener('mouseover', () => {
            link.style.textDecoration = 'underline';
          });
          link.addEventListener('mouseout', () => {
            link.style.textDecoration = 'none';
          });
        }
      });
      
      // Fix all paragraph text in the footer
      const footerText = footer.querySelectorAll('p');
      footerText.forEach(p => {
        if (p instanceof HTMLElement) {
          p.style.color = '#ffffff';
        }
      });
      
      // Fix footer headings
      const footerHeadings = footer.querySelectorAll('h5, h6');
      footerHeadings.forEach(heading => {
        if (heading instanceof HTMLElement) {
          heading.style.color = '#ffffff';
          heading.style.fontWeight = '600';
        }
      });
      
      // Fix contact labels
      const contactLabels = footer.querySelectorAll('.contact-label');
      contactLabels.forEach(label => {
        if (label instanceof HTMLElement) {
          label.style.color = '#ffffff';
          label.style.fontWeight = '500';
        }
      });
      
      // Fix hours text in list items
      const listItems = footer.querySelectorAll('li');
      listItems.forEach(item => {
        if (item instanceof HTMLElement) {
          item.style.color = '#ffffff';
        }
      });
      
      // Fix payment methods title
      const paymentTitle = footer.querySelector('.payment-title');
      if (paymentTitle && paymentTitle instanceof HTMLElement) {
        paymentTitle.style.color = '#ffffff';
        paymentTitle.style.fontWeight = '600';
      }
    }

    // Fix DMCA, Money Back Guarantee, and Cryptocurrency badges
    const guaranteeBadges = document.querySelectorAll('.dmca-green, .money-back-container, .crypto-container');
    guaranteeBadges.forEach(badge => {
      if (badge instanceof HTMLElement) {
        badge.style.backgroundColor = '#0e6a26';
        badge.style.color = '#ffffff';
        badge.style.fontWeight = '600';
        
        // Fix text inside badges
        const badgeText = badge.querySelectorAll('.guarantee-text, .crypto-text');
        badgeText.forEach(text => {
          if (text instanceof HTMLElement) {
            text.style.color = '#ffffff';
          }
        });
      }
    });

    // Fix cookie policy banner
    const cookieBanner = document.querySelector('.simple-cookie-banner');
    if (cookieBanner && cookieBanner instanceof HTMLElement) {
      cookieBanner.style.backgroundColor = '#343a40';
      cookieBanner.style.color = '#ffffff';
      
      // Fix links in cookie banner
      const cookieLinks = cookieBanner.querySelectorAll('a');
      cookieLinks.forEach(link => {
        if (link instanceof HTMLElement) {
          link.style.color = '#74c0fc';
          link.style.textDecoration = 'underline';
        }
      });
    }
  };

  // This component doesn't render anything visible
  return null;
};

export default ContrastFixer; 